// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private currentIndex = -1;
  private searchResults: Element[] = [];
  private searchTermSubject = new BehaviorSubject<string>('');
  private matchCountSubject = new BehaviorSubject<number>(0);
  private currentMatchSubject = new BehaviorSubject<number>(0);

  searchTerm$ = this.searchTermSubject.asObservable();
  matchCount$ = this.matchCountSubject.asObservable();
  currentMatch$ = this.currentMatchSubject.asObservable();

  search(term: string): void {
    this.clearHighlights();
    this.searchTermSubject.next(term);

    if (!term) {
      this.matchCountSubject.next(0);
      this.currentMatchSubject.next(0);
      return;
    }

    const searchRegex = new RegExp(term, 'gi');
    const textNodes = this.findTextNodes(
      document.querySelector('.featuresContent')
    );
    this.searchResults = [];

    textNodes.forEach((node) => {
      const text = node.textContent || '';
      if (text.match(searchRegex)) {
        const span = document.createElement('span');
        span.innerHTML = text.replace(
          searchRegex,
          (match) => `<mark class="search-highlight">${match}</mark>`
        );
        node.parentNode?.replaceChild(span, node);
        const highlights = span.getElementsByClassName('search-highlight');
        this.searchResults.push(...Array.from(highlights));
      }
    });

    this.matchCountSubject.next(this.searchResults.length);
    if (this.searchResults.length > 0) {
      this.currentIndex = 0;
      this.currentMatchSubject.next(1);
      this.scrollToMatch();
    }
  }

  private findTextNodes(element: Element | null): Text[] {
    const textNodes: Text[] = [];
    if (!element) return textNodes;

    const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

    let node;
    while ((node = walk.nextNode())) {
      textNodes.push(node as Text);
    }

    return textNodes;
  }

  private clearHighlights(): void {
    // First clear all highlight marks
    const highlights = document.getElementsByClassName('search-highlight');
    Array.from(highlights).forEach((highlight) => {
      const parent = highlight.parentNode;
      if (
        parent &&
        parent.nodeName === 'SPAN' &&
        parent.childNodes.length === 1
      ) {
        const text = highlight.textContent || '';
        const textNode = document.createTextNode(text);
        parent.parentNode?.replaceChild(textNode, parent);
      } else {
        const text = highlight.textContent || '';
        const textNode = document.createTextNode(text);
        highlight.parentNode?.replaceChild(textNode, highlight);
      }
    });

    // Then clear any remaining standalone spans in the search area
    const container = document.querySelector('.featuresContent');
    if (container) {
      const spans = container.getElementsByTagName('span');
      Array.from(spans).forEach((span) => {
        const text = span.textContent || '';
        const textNode = document.createTextNode(text);
        span.parentNode?.replaceChild(textNode, span);
      });
    }
  }

  navigateNext(): void {
    if (this.searchResults.length === 0) return;

    this.currentIndex = (this.currentIndex + 1) % this.searchResults.length;
    this.currentMatchSubject.next(this.currentIndex + 1);
    this.scrollToMatch();
  }

  navigatePrevious(): void {
    if (this.searchResults.length === 0) return;

    this.currentIndex =
      this.currentIndex <= 0
        ? this.searchResults.length - 1
        : this.currentIndex - 1;
    this.currentMatchSubject.next(this.currentIndex + 1);
    this.scrollToMatch();
  }

  private scrollToMatch(): void {
    const element = this.searchResults[this.currentIndex];
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.updateActiveMatch();
  }

  private updateActiveMatch(): void {
    document.querySelectorAll('.search-highlight').forEach((el, index) => {
      el.classList.toggle('active', index === this.currentIndex);
    });
  }
}
